import { useEffect, useState } from 'react';
import { useRoomContext } from '@livekit/components-react';
import { RoomEvent } from 'livekit-client';

/**
 * Subscribe to the LiveKit room's recording state — the single source of
 * truth shared by every participant.
 *
 * Why this exists: prior to this hook the recording indicator was driven
 * by a React state flag (`hasStartedRecording`) that only lived inside
 * the practitioner's `VideoCallScreen`. The client tab hard-coded the
 * flag to `false`, so a client could never reflect recording state even
 * when egress was actively capturing. Reading from `Room.isRecording`
 * fixes both directions: LiveKit broadcasts `RecordingStatusChanged` to
 * every participant whenever egress transitions, so practitioner +
 * client + observer tabs all observe the same boolean.
 *
 * Must be called inside a `<LiveKitRoom>` tree — that's where
 * `useRoomContext` resolves.
 */
export function useIsRecording(): boolean {
  const room = useRoomContext();
  const [isRecording, setIsRecording] = useState<boolean>(() => room.isRecording);

  useEffect(() => {
    const handler = (next: boolean) => {
      setIsRecording(next);
    };
    room.on(RoomEvent.RecordingStatusChanged, handler);
    return () => {
      room.off(RoomEvent.RecordingStatusChanged, handler);
    };
  }, [room]);

  return isRecording;
}
